/**
 * Share, from the PDApps window — one small modal for every tile.
 *
 * It shows the ONE link this person can pass on, says plainly who it opens for,
 * and offers Copy and a QR code:
 *
 *   own       an app of the org they're in   → its private app link (the default
 *                                              link: it opens only for people who
 *                                              already have access to the app, and
 *                                              runs as the app), else its Papan
 *                                              address
 *   link      an app they hold a link to     → that link (public: anyone; a link
 *                                              that names people: only them)
 *   elsewhere an app of another org / their  → its Papan address in that org
 *             Personal space
 *
 * An app in a Personal space has NO address to pass on: `/~/app/<id>` opens the
 * OPENER's own Personal space, and anyone else reaches the app through their own
 * shared copy (another id). So there the modal shows no link — only the way to make
 * one ("Share from Personal…", or the app's own private link where they can share it).
 *
 * For an app of the org's own drive the modal is "Share & access": below the link, a
 * Who has access row opens the drive's own Who has access dialog for the app folder —
 * offered to the app's managers only (the drive menu's rule); anyone else is told so.
 *
 * Where they may share the app here, "Share with people…" opens the full Share
 * dialog (links with their own audience). For an app of another org, "Share from
 * ‹Org›…" switches there and opens it. The modal decides no access: a link comes
 * from the server (the default link, or the link entry's address — judged again),
 * or is the app's Papan address, which opens only for people with access.
 */
import { useEffect, useState } from 'react'
import { Copy, Check, Globe, Lock, Link2, Building2, User, Users, QrCode, ArrowUpRight } from 'lucide-react'
import { parseMiniappIcon, parseMiniappIconRev } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { Modal, MiniappIcon, useToast } from '@papan/core-ui/ui'
import { ensureDefaultAppLink } from '@papan/share-links/client'
import { QrOverlay } from '@papan/share-links/ui'
import { linkAppAddress, type PdappEntry } from './appsData'

export type AppShareTarget =
  /** An app of the org they're in. `canShare`: they may manage its sharing here. */
  | { kind: 'own'; fileId: string; papanUrl: string | null; canShare: boolean; orgName: string | null }
  /** An app they reach through a link (a history entry, or one shared with them by link). */
  | { kind: 'link'; entry: PdappEntry }
  /** An app of another org, or of their Personal space while they're in an org. */
  | { kind: 'elsewhere'; papanUrl: string | null; orgName: string | null; personal: boolean }

export interface AppShareDialogProps {
  name: string
  icon: string | null
  /** The folder id an image icon is fetched by (an app of the active org only). */
  iconRef: string | null
  /** Where it's from, as the tile says it ("Acme", "Public link", "Personal"…). */
  source?: string
  target: AppShareTarget
  onClose: () => void
  /** `own` + canShare: open the full Share dialog. */
  onShareWithPeople?: () => void
  /** `elsewhere`: switch to that org and open its Share dialog there. */
  onShareFromThere?: () => void
  /**
   * An app of THIS org's drive: the modal is "Share & access", with a Who has access row.
   * `canManage` is the person's resolved permission on the app folder (undefined while it is
   * asked): only a manager opens the drive's Who has access dialog, as in the drive's own
   * menu; anyone else is told so. The server decides every change in that dialog.
   */
  access?: { canManage: boolean | undefined; onOpen: () => void }
}

/** `url` null: there is no address that opens for anyone else (an app in a Personal space). */
type Resolved = { url: string | null; audience: 'public' | 'restricted' | 'private' | 'papan' }

export function AppShareDialog({ name, icon, iconRef, source, target, onClose, onShareWithPeople, onShareFromThere, access }: AppShareDialogProps) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const [link, setLink] = useState<Resolved | null>(null)
  const [failed, setFailed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [qr, setQr] = useState<string | null>(null)

  // Asked once per app: the window re-renders (a list landing) with a new `target`
  // object for the same app, which must not ask again.
  const targetKey = target.kind === 'link'
    ? `link:${target.entry.source}:${target.entry.key}`
    : target.kind === 'own' ? `own:${target.fileId}:${target.canShare}` : `elsewhere:${target.papanUrl ?? `personal:${name}`}`
  useEffect(() => {
    let live = true
    setFailed(false)
    const done = (r: Resolved) => { if (live) { setLink(r); setFailed(false) } }
    if (target.kind === 'link') {
      linkAppAddress(target.entry)
        .then((r) => done({ url: r.url, audience: r.audience }))
        .catch(() => { if (live) setFailed(true) })
    } else if (target.kind === 'own' && target.canShare) {
      // The app's own default link — handed out only to someone who may share it.
      ensureDefaultAppLink(target.fileId)
        .then((r) => done(r.ok ? { url: r.link.url, audience: 'private' } : { url: target.papanUrl, audience: 'papan' }))
        .catch(() => done({ url: target.papanUrl, audience: 'papan' }))
    } else {
      done({ url: target.papanUrl, audience: 'papan' })
    }
    return () => { live = false }
    // `targetKey` names the app; the rest of `target` is read as it is now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey])

  const copy = async () => {
    if (!link?.url) return
    try {
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
      toast.success(t('appsWindow.shareDialog.copied'))
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      toast.error(t('appsWindow.actionFailed'))
    }
  }

  const where = target.kind === 'link' ? null : target.kind === 'elsewhere' && target.personal ? t('appsWindow.sections.personal') : target.orgName
  const card = (() => {
    if (link && !link.url) {
      return { Icon: User, title: t('appsWindow.shareDialog.personalOnly.title'), hint: t('appsWindow.shareDialog.personalOnly.hint') }
    }
    switch (link?.audience) {
      case 'public': return { Icon: Globe, title: t('appsWindow.shareDialog.public.title'), hint: t('appsWindow.shareDialog.public.hint') }
      case 'restricted': return { Icon: Link2, title: t('appsWindow.shareDialog.restricted.title'), hint: t('appsWindow.shareDialog.restricted.hint') }
      case 'private': return { Icon: Lock, title: t('appsWindow.shareDialog.private.title'), hint: t('appsWindow.shareDialog.private.hint') }
      default: return {
        Icon: target.kind === 'elsewhere' && target.personal ? User : Building2,
        title: t('appsWindow.shareDialog.papan.title'),
        hint: target.kind === 'elsewhere' && target.personal
          ? t('appsWindow.shareDialog.papan.hintPersonal')
          : where ? t('appsWindow.shareDialog.papan.hintOrg', { org: where }) : t('appsWindow.shareDialog.papan.hint'),
      }
    }
  })()

  const footer = (
    <>
      {onShareWithPeople && (
        <button type="button" onClick={onShareWithPeople} className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5 mr-auto" data-testid="apps-share-people">
          {t('appsWindow.shareDialog.withPeople')}
        </button>
      )}
      {onShareFromThere && (
        <button type="button" onClick={onShareFromThere} className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5 mr-auto" data-testid="apps-share-there">
          {where ? t('appsWindow.shareDialog.fromThere', { org: where }) : t('appsWindow.shareDialog.fromThereNoName')}
          <ArrowUpRight size={14} />
        </button>
      )}
      <button type="button" onClick={onClose} className="themed-btn-primary rounded-lg px-4 py-2 text-sm" data-testid="apps-share-done">
        {t('appsWindow.shareDialog.done')}
      </button>
    </>
  )

  return (
    <>
      <Modal title={access ? t('appsWindow.shareDialog.titleAccess', { name }) : t('appsWindow.shareDialog.title', { name })} onClose={onClose} footer={footer} maxWidth="max-w-md">
        <div className="px-5 py-4" data-testid="apps-share-dialog">
          <div className="flex items-center gap-3 mb-4">
            <MiniappIcon spec={parseMiniappIcon(icon)} name={name} size={40} refId={iconRef} rev={parseMiniappIconRev(icon)} className="shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium themed-text truncate">{name}</div>
              {source && <div className="text-xs themed-text-muted truncate">{source}</div>}
            </div>
          </div>
          <div className="rounded-xl border themed-border p-3" data-testid="apps-share-link" data-audience={link?.audience ?? ''}>
            {failed ? (
              <p className="text-sm themed-text-muted">{t('appsWindow.linkUnavailable')}</p>
            ) : !link ? (
              <p className="text-sm themed-text-muted">{t('appsWindow.loading')}</p>
            ) : !link.url ? (
              <div data-testid="apps-share-nolink">
                <div className="flex items-center gap-1.5 text-sm font-medium themed-text">
                  <card.Icon size={14} className="shrink-0 themed-text-muted" />
                  {card.title}
                </div>
                <p className="mt-1 text-xs themed-text-muted">{card.hint}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-sm font-medium themed-text">
                  <card.Icon size={14} className="shrink-0 themed-text-muted" />
                  {card.title}
                </div>
                <p className="mt-1 text-xs themed-text-muted">{card.hint}</p>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    readOnly
                    value={link.url}
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label={card.title}
                    className="flex-1 min-w-0 themed-input rounded-lg px-2.5 py-1.5 text-xs font-mono truncate"
                    data-testid="apps-share-url"
                  />
                  <button type="button" onClick={() => setQr(link.url)} className="ws-card-iconbtn" aria-label={t('appsWindow.shareDialog.qr')} title={t('appsWindow.shareDialog.qr')} data-testid="apps-share-qr">
                    <QrCode size={15} />
                  </button>
                  {/* Focus starts here (Modal honours autoFocus): Copy is what the modal is for. */}
                  <button type="button" autoFocus onClick={() => void copy()} className="themed-btn-secondary rounded-lg px-2.5 py-1.5 text-xs flex items-center gap-1" data-testid="apps-share-copy">
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {t('appsWindow.shareDialog.copy')}
                  </button>
                </div>
              </>
            )}
          </div>
          {access && (
            <div className="mt-3 rounded-xl border themed-border p-3 flex items-center gap-3" data-testid="apps-share-access">
              <Users size={14} className="shrink-0 themed-text-muted" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium themed-text">{t('appsWindow.shareDialog.access.title')}</div>
                <p className="mt-0.5 text-xs themed-text-muted">
                  {access.canManage === false ? t('appsWindow.shareDialog.access.managersOnly') : t('appsWindow.shareDialog.access.hint')}
                </p>
              </div>
              {access.canManage !== false && (
                <button type="button" onClick={access.onOpen} disabled={access.canManage === undefined}
                  className="themed-btn-secondary rounded-lg px-2.5 py-1.5 text-xs shrink-0 disabled:opacity-60" data-testid="apps-share-access-open">
                  {t('appsWindow.shareDialog.access.manage')}
                </button>
              )}
            </div>
          )}
        </div>
      </Modal>
      <QrOverlay url={qr} onClose={() => setQr(null)} />
    </>
  )
}
